// Sharing a fiche. A guest who loves something Médard showed them can send it
// on — the link deep-links straight back to THAT fiche, and the crawler card
// is the fiche itself (the backend rewrites the OG tags per fiche; see
// backend/og_cards.py). Uses the native Web Share sheet where available
// (mobile), else copies the link.
//
// Monuments could be shared since the W1 sheet; the owner asked for the rest
// (2026-08-02: « sur les monuments les personnages on peut le partager
// celle-ci, je n'ai pas vu de partage encore »). Every family now travels.
//
// Pure + dependency-free so it unit-tests without the sheet/toast/React stack.

/**
 * The query parameter that carries each fiche family. Insertion order IS the
 * precedence order used when a URL somehow carries several (a hand-edited or
 * concatenated link): first match wins, deterministically.
 *
 * These strings are a PUBLIC CONTRACT — they are printed on QR codes and live
 * in links guests have already sent. Rename one and those links go dark.
 */
export const FICHE_PARAMS = Object.freeze({
    monument: 'monument',
    personnage: 'personnage',
    tresor: 'tresor',
    evenement: 'evenement',
});

export const FICHE_KINDS = Object.freeze(Object.keys(FICHE_PARAMS));

function shareOrigin(origin) {
    if (origin != null) return origin;
    if (typeof window !== 'undefined' && window.location) return window.location.origin;
    return '';
}

/**
 * Canonical shareable deep link to a fiche: <origin>/?<param>=<id>.
 * `origin` is injected for testability; defaults to the current window origin.
 * An unknown kind returns '' rather than a link to nowhere — a share button
 * must never hand a guest a URL that opens the home page pretending to be a
 * fiche.
 */
export function buildFicheShareUrl(kind, id, origin, lang) {
    const param = FICHE_PARAMS[kind];
    if (!param) return '';
    const base = `${shareOrigin(origin)}/?${param}=${encodeURIComponent(id || '')}`;
    // The guest's language rides along, so a fiche sent by an English guest
    // opens in English AND unfurls in English (the crawler card is built from
    // this same URL, server-side). French is the default — no parameter.
    return lang && lang !== 'fr' ? `${base}&lang=${encodeURIComponent(lang)}` : base;
}

/**
 * Canonical shareable deep link to a monument fiche: <origin>/?monument=<id>.
 * Kept as its own name because it is the one links already in the wild use.
 */
export function buildMonumentShareUrl(id, origin) {
    return buildFicheShareUrl('monument', id, origin);
}

/**
 * Read a fiche deep link out of a query string (`window.location.search` or a
 * bare 'a=b' string). Returns `{ kind, id }` or null.
 *
 * One parser, one precedence, one place to test — rather than four effects
 * each doing their own `searchParams.get`.
 */
export function readFicheDeepLink(search) {
    if (typeof search !== 'string' || !search) return null;
    let params;
    try {
        params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    } catch {
        return null;
    }
    for (const kind of FICHE_KINDS) {
        const id = params.get(FICHE_PARAMS[kind]);
        // An empty value ('?tresor=') is not a fiche — fall through to the
        // next family rather than opening a gallery on a blank id.
        if (id) return { kind, id };
    }
    return null;
}

/**
 * Share `{ title, text, url }` via the Web Share API, falling back to copying
 * the URL to the clipboard. Returns one of:
 *   'shared'  — the native share sheet handled it (incl. a user cancel),
 *   'copied'  — no Web Share; the link was copied instead,
 *   'failed'  — neither path was available.
 * Never throws — a share affordance must never break the fiche.
 */
export async function shareLink({ title, text, url }, nav) {
    const n = nav || (typeof navigator !== 'undefined' ? navigator : undefined);
    if (n && typeof n.share === 'function') {
        try {
            await n.share({ title, text, url });
            return 'shared';
        } catch (e) {
            // A user dismissing the native sheet raises AbortError — that is a
            // completed interaction, NOT a reason to fall back to copying.
            if (e && e.name === 'AbortError') return 'shared';
            // A real Web Share failure falls through to the clipboard path.
        }
    }
    if (n && n.clipboard && typeof n.clipboard.writeText === 'function') {
        try {
            await n.clipboard.writeText(url);
            return 'copied';
        } catch {
            /* fall through to failed */
        }
    }
    return 'failed';
}
